import React, { useState } from 'react'
import { Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment, List, ListItem, ListItemAvatar, ListItemText, Stack, TextField, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import { selectWhatsappState } from '../../state/whatsapp/whatsappSelector';
import Styles from './whatsapp.module.scss'
import WhatsappTemplateModal from './WhatsappTemplateModal';
import { getUserList, sendGenericTemplateMessage } from '../../state/whatsapp/whatsappSlice';
import { getPreviewBody, getVariablesFromBody } from '../../helpers/utils/textFormatter';

const WhatsappSidebar = ({ selectedContact, onSelectContact }) => {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState('');
    const { userList, isUserListLoading, isSending, templates } = useSelector(selectWhatsappState);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [filterStatus, setFilterStatus] = useState(null); // 'replied', 'not_replied', or null

    const filteredContacts = userList.filter((contact) => {
        const matchesSearch = contact.phone.toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;

        if (filterStatus === 'replied') {
            return contact.status?.toLowerCase() === 'delivered';
        }
        if (filterStatus === 'not_replied') {
            return contact.status?.toLowerCase() === 'received';
        }
        return true;
    });

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        if (isToday) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    const formatPhone = (phone) => {
        return phone ? phone.replace('whatsapp:', '') : '';
    };

    const handleRefresh = () => {
        dispatch(getUserList());
    }

    const handleSendTemplateMessage = (templateVariables, selectedTemplateId, phoneNumber) => {
        const activeTemplate = templates.find(t => t.sid === selectedTemplateId);
        if (!activeTemplate) return;

        const toPhone = phoneNumber.replace('whatsapp:', '');
        const formattedVars = {};
        const varKeys = activeTemplate.variables && Object.keys(activeTemplate.variables).length > 0
            ? Object.keys(activeTemplate.variables).sort((a, b) => Number(a) - Number(b))
            : getVariablesFromBody(activeTemplate.body);

        varKeys.forEach(k => {
            formattedVars[k] = templateVariables[k] || "";
        });

        const bodyPreview = getPreviewBody(activeTemplate.body, templateVariables);
        dispatch(sendGenericTemplateMessage({
            to: toPhone,
            templateSid: activeTemplate.sid,
            variables: formattedVars,
            body: bodyPreview
        }));

        setIsTemplateModalOpen(false);
    }
    return (
        <Box className={Styles.sidebar}>
            <Box className={Styles.sidebarHeader} sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search contacts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsTemplateModalOpen(true)}
                    >
                        <AddIcon />
                    </Button>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} mt={1} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip 
                            label="Replied" 
                            variant={filterStatus === 'replied' ? 'filled' : 'outlined'} 
                            color='success' 
                            onClick={() => setFilterStatus(prev => prev === 'replied' ? null : 'replied')}
                        />
                        <Chip 
                            label="Not Replied" 
                            variant={filterStatus === 'not_replied' ? 'filled' : 'outlined'} 
                            color='warning' 
                            onClick={() => setFilterStatus(prev => prev === 'not_replied' ? null : 'not_replied')}
                        />
                    </Stack>
                    <Button onClick={handleRefresh} variant="outlined" color="primary" size='small'>
                        <RefreshIcon />
                    </Button>
                </Stack>
                <Divider sx={{ mt: 2 }} />
            </Box>
            {isUserListLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <List disablePadding>
                    {filteredContacts.map((contact) => (
                        <React.Fragment key={contact.messageSid}>
                            <ListItem
                                className={`${Styles.sidebarItem} ${selectedContact?.phone === contact.phone ? Styles.sidebarItemActive : ''}`}
                                onClick={() => onSelectContact(contact)}
                                alignItems="flex-start"
                                sx={{ px: 2, py: 1.5 }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: '#25D366', color: '#fff' }} />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle2" noWrap>
                                                {formatPhone(contact.phone)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTime(contact.messageAt)}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '70%' }}>
                                                {contact.lastMessage}
                                            </Typography>
                                            {contact.status && (
                                                <Chip
                                                    label={contact.status}
                                                    size="small"
                                                    sx={{
                                                        fontSize: 10,
                                                        height: 20,
                                                        bgcolor: contact.status === 'delivered' ? '#e8f5e9'
                                                            : contact.status === 'read' ? '#e3f2fd'
                                                                : (contact.status === 'undelivered' || contact.status === 'failed') ? '#fce4ec'
                                                                    : '#f0ea9cff',
                                                        color: contact.status === 'delivered' ? '#2e7d32'
                                                            : contact.status === 'read' ? '#1565c0'
                                                                : (contact.status === 'undelivered' || contact.status === 'failed') ? '#c62828'
                                                                    : '#616161ff',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                    {filteredContacts.length === 0 && !isUserListLoading && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No contacts found
                            </Typography>
                        </Box>
                    )}
                </List>
            )}

            <WhatsappTemplateModal
                title="Create Conversation"
                isLoading={isSending}
                open={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                onSubmit={handleSendTemplateMessage}
            />
        </Box>
    )
}

export default WhatsappSidebar
